import React, { useEffect, Component } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, notification, Button, Typography } from 'antd';
import { useAuth } from './contexts/AuthContext';
import { usePermission } from './hooks/usePermission';
import { antdTheme } from './theme/antd-theme';
import Login from './pages/Login';
import StatusPage from './pages/Status';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import AgentWorkspace from './pages/AgentWorkspace';
import Businesses from './pages/Businesses';
import BusinessDetail from './pages/BusinessDetail';
import CreateBusiness from './pages/CreateBusiness';
import Users from './pages/Users';
import Equipment from './pages/Equipment';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import ResetPassword from './pages/ResetPassword';
import RequestAccount from './pages/RequestAccount';
import Calls from './pages/Calls';
import RemoteAccess from './pages/RemoteAccess';
import AgentCallCenter from './pages/AgentCallCenter';
import Feedback from './pages/Feedback';
import FeedbackThankYou from './pages/FeedbackThankYou';
import FeedbackDashboard from './pages/FeedbackDashboard';
import TVDashboard from './pages/TVDashboard';
import SalesPortal from './pages/SalesPortal';
import SalesPortalAdmin from './components/SalesPortalAdmin';
import AuditLogs from './pages/AuditLogs';
import SearchResults from './pages/SearchResults';
import Layout from './components/Layout';
import { AgentWorkspaceTabsProvider } from './contexts/AgentWorkspaceTabsContext';
import { StandaloneChatShell } from './components/StandaloneChatShell';
import LoadingSpinner from './components/LoadingSpinner';
import { MobileChatPwaBanner } from './components/MobileChatPwaBanner';

/** Catches render errors in Users page so we show a fallback instead of a generic Script error. */
class UsersErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Users] Error:', error?.message, error?.stack);
    console.error('[Users] Component stack:', errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      return (
        <div style={{ padding: 24, maxWidth: 600, margin: '24px auto', textAlign: 'center' }}>
          <Typography.Title level={4} type="danger">Something went wrong on the Users page</Typography.Title>
          {err?.message && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8, fontFamily: 'monospace', fontSize: 12, textAlign: 'left', wordBreak: 'break-all' }}>
              {err.message}
            </Typography.Paragraph>
          )}
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Ensure the backend is running on port 5000. Reload the page to try again.
          </Typography.Paragraph>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button type="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button onClick={() => window.location.href = '/dashboard'}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Catches render errors in AgentWorkspace so we show a fallback instead of a white screen. */
class AgentWorkspaceErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[AgentWorkspace] Error:', error?.message, error?.stack);
    console.error('[AgentWorkspace] Component stack:', errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      return (
        <div style={{ padding: 24, maxWidth: 600, margin: '24px auto', textAlign: 'center' }}>
          <Typography.Title level={4} type="danger">Something went wrong loading this ticket</Typography.Title>
          {err?.message && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8, fontFamily: 'monospace', fontSize: 12, textAlign: 'left', wordBreak: 'break-all' }}>
              {err.message}
            </Typography.Paragraph>
          )}
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Reload the page to try again, or go back to the ticket list.
          </Typography.Paragraph>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button type="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button onClick={() => window.location.href = '/tickets'}>
              Back to Tickets
            </Button>
            {err?.stack && (
              <Button onClick={() => { navigator.clipboard?.writeText(err.stack || ''); }}>Copy error details</Button>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const { user, loading } = useAuth();
  const canManageReplacements = usePermission('manage_replacements');
  const canAnalytics = usePermission('analytics_reports');
  const canFeedback = usePermission('feedback');
  const canAuditLogs = usePermission('audit_logs');
  const canSalesPortalMgmt = usePermission('sales_agent_portal_management');
  const canAccessChat = usePermission('access_chat');

  // Set default page title
  useEffect(() => {
    document.title = 'Vitreous Support - Support Ticket Platform';
  }, []);

  // PWA: when launched in standalone (e.g. from home screen) at root, open chat
  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone && window.location.pathname === '/' && !window.location.search) {
      window.location.replace('/chat');
    }
  }, []);

  // Global notification config: top right, auto-close in 5 seconds
  useEffect(() => {
    notification.config({
      placement: 'topRight',
      duration: 5,
      top: 80,
    });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return (
      <ConfigProvider theme={antdTheme}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/request-account" element={<RequestAccount />} />
          <Route path="/feedback/:token" element={<Feedback />} />
          <Route path="/feedback/thank-you" element={<FeedbackThankYou />} />
          <Route path="/tvdashboard" element={<TVDashboard />} />
          <Route path="/status" element={<StatusPage />} />
          {/* Chat as its own site: send to login with returnUrl so they land back on /chat */}
          <Route path="/chat" element={<Navigate to="/login?returnUrl=%2Fchat" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <MobileChatPwaBanner />
      <Routes>
        {/* TV Dashboard - No Layout/Sidebar */}
        <Route path="/tvdashboard" element={<TVDashboard />} />
        <Route path="/status" element={<StatusPage />} />
        {/* Chat - admins and agents have access by default; others enabled via Permissions tab */}
        {canAccessChat && (
          <Route path="/chat" element={<StandaloneChatShell />} />
        )}
        
        {/* All other routes with Layout */}
        <Route path="*" element={
          <AgentWorkspaceTabsProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:id" element={<TicketDetail />} />
              <Route path="/workspace/number/:ticketNumber" element={<AgentWorkspaceErrorBoundary><AgentWorkspace /></AgentWorkspaceErrorBoundary>} />
              <Route path="/workspace/:id" element={<AgentWorkspaceErrorBoundary><AgentWorkspace /></AgentWorkspaceErrorBoundary>} />
              <Route path="/businesses" element={<Businesses />} />
              <Route path="/businesses/new" element={<CreateBusiness />} />
              <Route path="/businesses/:id/edit" element={<CreateBusiness />} />
              <Route path="/businesses/:id" element={<BusinessDetail />} />
              {(user.role === 'admin' || canManageReplacements) && (
                <Route path="/equipment" element={<Equipment />} />
              )}
              {user.role === 'admin' && (
                <>
                  <Route path="/users" element={<UsersErrorBoundary><Users /></UsersErrorBoundary>} />
                  <Route path="/settings" element={<Settings />} />
                </>
              )}
              {(user.role === 'admin' || canAnalytics) && (
                <Route path="/analytics" element={<Analytics />} />
              )}
              {(user.role === 'admin' || canFeedback) && (
                <Route path="/feedback-dashboard" element={<FeedbackDashboard />} />
              )}
              {(user.role === 'admin' || canAuditLogs) && (
                <Route path="/audit-logs" element={<AuditLogs />} />
              )}
              <Route path="/profile" element={<Profile />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/call-center" element={<AgentCallCenter />} />
              <Route path="/calls" element={<Calls />} />
              <Route path="/remote" element={<RemoteAccess />} />
              {(user.role === 'admin' || canSalesPortalMgmt) && (
                <Route path="/salesportal" element={<SalesPortalAdmin />} />
              )}
              {(user.role === 'sales' || user.role === 'sales_agent' || user.role === 'sales_rep_internal') && (
                <Route path="/salesportal" element={<SalesPortal />} />
              )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
          </AgentWorkspaceTabsProvider>
        } />
      </Routes>
    </ConfigProvider>
  );
}

export default App;
